"use client";

import { useState, useCallback, useRef } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";

interface FileUploadProps {
  maxFiles?: number;
  accept?: string;
  onChange?: (files: File[]) => void;
  className?: string;
}

export function FileUpload({
  maxFiles = 5,
  accept = "image/*",
  onChange,
  className = "",
}: FileUploadProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = useCallback(
    (newFiles: FileList | File[]) => {
      const fileArray = Array.from(newFiles);
      const remaining = maxFiles - files.length;
      const toAdd = fileArray.slice(0, remaining);

      if (toAdd.length === 0) return;

      const updated = [...files, ...toAdd];
      setFiles(updated);
      onChange?.(updated);

      // Generate previews
      toAdd.forEach((file) => {
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setPreviews((prev) => [...prev, e.target?.result as string]);
          };
          reader.readAsDataURL(file);
        } else {
          setPreviews((prev) => [...prev, ""]);
        }
      });
    },
    [files, maxFiles, onChange]
  );

  const removeFile = useCallback(
    (index: number) => {
      const updated = files.filter((_, i) => i !== index);
      const updatedPreviews = previews.filter((_, i) => i !== index);
      setFiles(updated);
      setPreviews(updatedPreviews);
      onChange?.(updated);
    },
    [files, previews, onChange]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles]
  );

  return (
    <div className={className}>
      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
          dragOver
            ? "border-em-blue bg-em-blue/5"
            : "border-border hover:border-em-blue/40 hover:bg-secondary/50"
        } ${files.length >= maxFiles ? "opacity-50 pointer-events-none" : ""}`}
      >
        <Upload
          className={`h-8 w-8 ${
            dragOver ? "text-em-blue" : "text-muted-foreground"
          }`}
        />
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">
            Drag & drop photos here
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            or click to browse ({files.length}/{maxFiles})
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          onChange={(e) => {
            if (e.target.files) addFiles(e.target.files);
            e.target.value = "";
          }}
          className="hidden"
        />
      </div>

      {/* Preview grid */}
      {files.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
          {files.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              className="relative group aspect-square rounded-lg overflow-hidden border border-border bg-secondary"
            >
              {previews[i] ? (
                // Data URL previews are local FileReader output, not image assets.
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={previews[i]}
                  alt={file.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="h-6 w-6 text-muted-foreground" />
                </div>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(i);
                }}
                className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="h-3 w-3" />
              </button>
              <p className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-black/50 text-white text-[10px] truncate">
                {file.name}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
